/**
 * Deserializes Jupyter Notebook v4 (.ipynb) JSON into NotebookTabContent.
 *
 * Features:
 * - Tolerant source parsing: accepts both string and string[]
 * - Imports code cell outputs from rich save mode
 * - Non-SQL cells imported as Markdown with warning badge
 * - Polyglot Notebooks support (SQL/JS preserved, C#/F# → markdown)
 * - Preserves unknown metadata and cell fields for round-trip
 */

import type {
  NotebookTabContent,
  NotebookCell,
  NotebookCodeCell,
  NotebookJavaScriptCell,
  NotebookMarkdownCell,
  NotebookSaveMode,
} from '../types/notebookTypes';
import type { ChartConfig } from '../../charts/types/chartTypes';
import { deserializeCellOutputs } from './ipynbOutputDeserializer';
import type { RawIpynbOutput } from './ipynbOutputDeserializer';

// ============================================================================
// .ipynb Raw Types (from file)
// ============================================================================

type RawIpynbNotebook = {
  nbformat?: number;
  metadata?: RawIpynbMetadata;
  cells?: RawIpynbCell[];
  [key: string]: unknown;
};

type RawIpynbMetadata = {
  kernelspec?: { name?: string; display_name?: string; language?: string };
  language_info?: { name?: string };
  jam_sql?: { version?: number; saveMode?: string; connectionName?: string; engine?: string };
  polyglot_notebook?: Record<string, unknown>;
  [key: string]: unknown;
};

type RawIpynbCell = {
  cell_type?: string;
  id?: string;
  source?: string | string[];
  metadata?: Record<string, unknown>;
  outputs?: RawIpynbOutput[];
  execution_count?: number | null;
  [key: string]: unknown;
};

// ============================================================================
// Deserialization Result
// ============================================================================

export type DeserializeResult = {
  content: NotebookTabContent;
  /** Preserved raw notebook for round-trip (unknown fields, metadata) */
  preservedNotebook: Record<string, unknown>;
  /** Warnings generated during import */
  warnings: string[];
};

// ============================================================================
// Public API
// ============================================================================

/** Deserialize .ipynb JSON string into NotebookTabContent. */
export function deserializeFromIpynb(jsonString: string): DeserializeResult {
  const raw = JSON.parse(jsonString) as RawIpynbNotebook;
  return deserializeNotebook(raw);
}

/** Deserialize a parsed .ipynb object into NotebookTabContent. */
export function deserializeNotebook(raw: RawIpynbNotebook): DeserializeResult {
  const warnings: string[] = [];

  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid .ipynb file: expected a JSON object');
  }
  if (!Array.isArray(raw.cells)) {
    throw new Error('Invalid .ipynb file: missing "cells" array');
  }

  const metadata = raw.metadata ?? {};
  const jamSql = metadata.jam_sql;
  const saveMode: NotebookSaveMode = jamSql?.saveMode === 'clean' ? 'clean' : 'rich';

  const cells: NotebookCell[] = [];
  for (let i = 0; i < raw.cells.length; i++) {
    const result = deserializeCell(raw.cells[i], i, metadata);
    cells.push(result.cell);
    if (result.warning) warnings.push(result.warning);
  }

  const content: NotebookTabContent = {
    cells,
    metadata: {
      version: 1,
      saveMode,
      connectionName: jamSql?.connectionName,
      engine: jamSql?.engine,
    },
    executionCounter: 0,
    isDirty: false,
  };

  const { cells: _cells, ...rest } = raw;
  const preservedNotebook: Record<string, unknown> = { ...rest };

  return { content, preservedNotebook, warnings };
}

// ============================================================================
// Cell Deserialization
// ============================================================================

/** Languages treated as SQL (case-insensitive) */
const SQL_LANGUAGES = new Set(['sql', 't-sql', 'tsql', 'plpgsql', 'plsql', 'mysql']);

/** Languages treated as JavaScript */
const JS_LANGUAGES = new Set(['javascript', 'js']);

/** Polyglot non-SQL/JS languages that become markdown */
const POLYGLOT_NON_SQL_LANGUAGES = new Set([
  'csharp', 'fsharp', 'powershell', 'html', 'kql', 'python', 'r',
]);

type CellResult = { cell: NotebookCell; warning?: string };

function deserializeCell(
  rawCell: RawIpynbCell, index: number, notebookMetadata: RawIpynbMetadata
): CellResult {
  const cellType = rawCell.cell_type ?? 'code';
  const source = joinSource(rawCell.source);
  const cellId = rawCell.id || crypto.randomUUID();

  if (cellType === 'markdown') {
    return { cell: mkMarkdown(cellId, source) };
  }

  if (cellType === 'raw') {
    return { cell: mkMarkdown(cellId, source), warning: `Cell ${index + 1}: "raw" cell type imported as Markdown` };
  }

  if (cellType === 'code') {
    const lang = detectCellLanguage(rawCell, notebookMetadata);
    const chartConfig = extractChartConfig(rawCell);

    // JavaScript cells
    if (lang && JS_LANGUAGES.has(lang)) {
      const outputs = deserializeCellOutputs(rawCell.outputs);
      const execOrder = typeof rawCell.execution_count === 'number' ? rawCell.execution_count : undefined;
      const cell: NotebookJavaScriptCell = {
        id: cellId, cellType: 'javascript', source, executionState: 'idle',
        ...(outputs ? { outputs } : {}),
        ...(execOrder !== undefined ? { executionOrder: execOrder } : {}),
        ...(chartConfig ? { chartConfig } : {}),
      };
      return { cell };
    }

    // Non-SQL/JS languages → markdown
    if (lang && POLYGLOT_NON_SQL_LANGUAGES.has(lang)) {
      const wrapped = `<!-- Original language: ${lang} -->\n\`\`\`${lang}\n${source}\n\`\`\``;
      return { cell: mkMarkdown(cellId, wrapped), warning: `Cell ${index + 1}: ${lang} cell imported as Markdown (not supported)` };
    }

    // SQL cells (default)
    const outputs = deserializeCellOutputs(rawCell.outputs);
    const execOrder = typeof rawCell.execution_count === 'number' ? rawCell.execution_count : undefined;
    const cell: NotebookCodeCell = {
      id: cellId, cellType: 'code', source, executionState: 'idle',
      ...(outputs ? { outputs } : {}),
      ...(execOrder !== undefined ? { executionOrder: execOrder } : {}),
      ...(chartConfig ? { chartConfig } : {}),
    };
    return { cell };
  }

  return { cell: mkMarkdown(cellId, source), warning: `Cell ${index + 1}: unknown cell type "${cellType}" imported as Markdown` };
}

function mkMarkdown(id: string, source: string): NotebookMarkdownCell {
  return { id, cellType: 'markdown', source, isEditing: false };
}

// ============================================================================
// Helpers
// ============================================================================

/** Extract chart config from cell metadata if present. */
function extractChartConfig(rawCell: RawIpynbCell): ChartConfig | undefined {
  const jamSql = (rawCell.metadata as Record<string, unknown>)?.jam_sql;
  if (!jamSql || typeof jamSql !== 'object') return undefined;
  const config = (jamSql as Record<string, unknown>)?.chartConfig;
  if (!config || typeof config !== 'object') return undefined;
  // Basic shape validation — must have type, xAxis, yAxis
  const c = config as Record<string, unknown>;
  if (typeof c.type !== 'string' || !c.xAxis || !Array.isArray(c.yAxis)) return undefined;
  return config as ChartConfig;
}

/** Join source lines — accepts both string and string[]. */
export function joinSource(source: string | string[] | undefined): string {
  if (!source) return '';
  if (typeof source === 'string') return source;
  return source.join('');
}

function detectCellLanguage(cell: RawIpynbCell, meta: RawIpynbMetadata): string | undefined {
  // Jam SQL: cell.metadata.jam_sql.language
  const jamSql = (cell.metadata as Record<string, unknown>)?.jam_sql;
  if (jamSql && typeof jamSql === 'object') {
    const lang = (jamSql as Record<string, unknown>)?.language;
    if (typeof lang === 'string') return lang.toLowerCase();
  }

  // Polyglot: cell.metadata.polyglot_notebook.kernelName
  const polyKernel = (cell.metadata as Record<string, unknown>)?.polyglot_notebook;
  if (polyKernel && typeof polyKernel === 'object') {
    const name = (polyKernel as Record<string, unknown>)?.kernelName;
    if (typeof name === 'string') return name.toLowerCase();
  }

  // Polyglot: cell.metadata.dotnet_interactive.language
  const dotnet = (cell.metadata as Record<string, unknown>)?.dotnet_interactive;
  if (dotnet && typeof dotnet === 'object') {
    const lang = (dotnet as Record<string, unknown>)?.language;
    if (typeof lang === 'string') return lang.toLowerCase();
  }

  // Fallback to notebook-level kernelspec
  const kernelLang = meta.kernelspec?.language?.toLowerCase();
  if (kernelLang && !SQL_LANGUAGES.has(kernelLang)) return kernelLang;

  return undefined;
}
